import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, MapPin, Train, Plane, Car } from 'lucide-react';

export default function Location() {
  const [locationData, setLocationData] = useState(null);

  useEffect(() => {
    fetch('/data/location.json')
      .then(res => res.json())
      .then(data => setLocationData(data))
      .catch(err => console.error('Error loading location:', err));
  }, []);

  if (!locationData) return null;

  return (
    <section
      id="location"
      data-testid="location-section"
      className="py-10 sm:py-12 md:py-16 bg-white lg:mr-72 xl:mr-80"
    >
      <div className="max-w-5xl mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-10"
        >
          <h2 className="font-serif text-xl sm:text-2xl md:text-3xl text-[#9A7B4F] mb-2">
            Strategic Location - Upper Kharghar
          </h2>
          <div className="w-12 sm:w-16 h-1 bg-[#9A7B4F] mx-auto" />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {/* Location Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative">
              <img
                src="/media/sun_city_hero1.webp"
                alt="Sai Sun City Location"
                className="w-full h-auto"
              />
              <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 flex items-center gap-2 bg-white shadow-lg p-2 sm:p-3">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-[#9A7B4F]" />
                <div>
                  <p className="text-gray-900 font-medium text-xs sm:text-sm">Sai Sun City</p>
                  <p className="text-gray-500 text-[10px] sm:text-xs">Upper Kharghar, Navi Mumbai</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Connectivity */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-xs uppercase tracking-wider text-[#9A7B4F] mb-3 sm:mb-4 flex items-center gap-2">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
              Connectivity
            </h3>
            <div className="space-y-2">
              {locationData.connectivity.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-gray-50 border border-gray-100">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#9A7B4F]/10 flex items-center justify-center flex-shrink-0">
                    {item.place.includes('Airport') ? (
                      <Plane className="w-3 h-3 sm:w-4 sm:h-4 text-[#9A7B4F]" />
                    ) : item.place.includes('Railway') || item.place.includes('Metro') ? (
                      <Train className="w-3 h-3 sm:w-4 sm:h-4 text-[#9A7B4F]" />
                    ) : (
                      <Car className="w-3 h-3 sm:w-4 sm:h-4 text-[#9A7B4F]" />
                    )}
                  </div>
                  <p className="flex-1 text-gray-700 text-xs sm:text-sm">{item.place}</p>
                  <span className="font-serif text-[#9A7B4F] font-bold text-sm sm:text-base">{item.time}</span>
                </div>
              ))}
            </div>

            <div className="mt-4 sm:mt-6">
              <h4 className="text-[10px] sm:text-xs uppercase tracking-wider text-[#9A7B4F] mb-2">Nearby</h4>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {locationData.nearby.map((place, idx) => (
                  <span key={idx} className="px-2 sm:px-3 py-1 bg-gray-100 text-[10px] sm:text-sm text-gray-600">
                    {place}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
